import {useQuery} from "@tanstack/react-query";
import crmService, {CustomerViewResponse} from "../../../../service/crmService.ts";
import {useTranslation} from "react-i18next";
import {useParams} from "react-router";
import {useGarageScope} from "../../../../hooks/useGarageScope.ts";
import FrameLoad from "../../../../components/layout/FrameLoad.tsx";
import {Alert} from "@mui/material";
import DeletedCustomerMain from "./DeletedCustomerMain.tsx";
import BasePage from "../../../../components/layout/BasePage.tsx";

interface DeletedCustomerPageProps {
    navigateBackTo: string;
}

const DeletedCustomerPage = (props: DeletedCustomerPageProps) => {
    const {t} = useTranslation();
    const {customerNo} = useParams();
    const {garageNo, garageGroupNo} = useGarageScope();

    const queryKey = ["customer", "view", customerNo, garageNo, garageGroupNo];
    const {data, isLoading, error} = useQuery({
        queryKey,
        queryFn: async () => await crmService.viewCustomer(customerNo!) as CustomerViewResponse,
        staleTime: 5 * 60 * 1000, // deleted customers do not change, so we can use cache
    });

    // Mail history is loaded alongside the master data; a failure here must not
    // block the (more important) customer view, so the table just stays empty.
    const {data: communicationsData, isLoading: communicationsLoading} = useQuery({
        queryKey: ["customer", "communications", customerNo],
        queryFn: async () => await crmService.getCustomerCommunications(customerNo!),
        staleTime: 5 * 60 * 1000,
    });

    const customerViewDetails = data?.customer;

    let MainContent;
    if (isLoading || !customerViewDetails) {
        MainContent = <FrameLoad error={error ?? undefined}/>;
    } else if (error) {
        MainContent = <Alert severity={"error"}>{error.message}</Alert>;
    } else {
        MainContent = <DeletedCustomerMain
            customer={customerViewDetails}
            communications={communicationsData?.communications ?? []}
            communicationsLoading={communicationsLoading}
            sendCron={communicationsData?.sendCron}
            navigateBackTo={props.navigateBackTo}
        />;
    }

    const title = !customerNo ? t("crm.create") : `${customerViewDetails?.firstName} ${customerViewDetails?.lastName}`;

    return (
        <BasePage
            title={title}
            mainContent={MainContent}
        >
        </BasePage>
    );
};

export default DeletedCustomerPage;
